import { SubscriptionPlan, Currency } from 'store/api/enums';
import { Plan } from 'store/billing/models';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';

export interface NextPlanPriceInfo {
  price: string | number | null;
  currency: string | null;
}

/**
 * Gets the price and currency of the next higher plan based on the current subscription
 * @param currentSubscription - The current subscription object
 * @param getFeaturesOfPlans - Function that returns available plans
 * @returns Object containing price and currency of the next plan, or null values if not available
 */
export const getNextPlanPrice = (
  currentSubscription?: CurrentSubscription,
  getFeaturesOfPlans?: () => Plan[]
): NextPlanPriceInfo => {
  if (!currentSubscription?.description || !getFeaturesOfPlans) {
    return { price: null, currency: null };
  }

  const currentPlanName = currentSubscription.description;
  const plans = getFeaturesOfPlans();

  if (!Array.isArray(plans)) {
    console.error('getFeaturesOfPlans did not return an array:', plans);
    return { price: null, currency: null };
  }

  // Define the plan hierarchy
  const planHierarchy = {
    [SubscriptionPlan.SMALL_PLAN]: SubscriptionPlan.MEDIUM_PLAN,
    [SubscriptionPlan.MEDIUM_PLAN]: SubscriptionPlan.LARGE_PLAN,
    [SubscriptionPlan.LARGE_PLAN]: SubscriptionPlan.ENTERPRISE_PLAN,
  };

  // Get the next plan name
  const nextPlanName = planHierarchy[currentPlanName];

  if (!nextPlanName) {
    return { price: null, currency: null }; // Already at highest plan
  }

  // Find the next plan in the available plans
  const nextPlan = plans.find(
    (plan) => plan.planName.toLowerCase() === nextPlanName.toLowerCase()
  );

  if (!nextPlan) {
    return { price: null, currency: null };
  }

  // Handle currency - ensure it's a string and convert to uppercase
  let currency = 'EUR'; // default fallback
  if (currentSubscription.currency) {
    if (typeof currentSubscription.currency === 'string') {
      currency = currentSubscription.currency.toUpperCase();
    } else if (typeof currentSubscription.currency === 'object') {
      console.warn(
        'Currency is an object, extracting value:',
        currentSubscription.currency
      );
      const currencyObj = currentSubscription.currency as any;
      if (currencyObj.EUR) {
        currency = 'EUR';
      } else if (currencyObj.AED) {
        currency = 'AED';
      } else if (currencyObj.USD) {
        currency = 'USD';
      }
    }
  }

  // Extract the price for the current currency from the plan
  let price: string | number | null = null;
  if (nextPlan.price && typeof nextPlan.price === 'object') {
    const priceObj = nextPlan.price as any;
    const extractedPrice = priceObj[currency];
    if (extractedPrice !== undefined) {
      price = extractedPrice;
    }
  } else if (
    typeof nextPlan.price === 'string' ||
    typeof nextPlan.price === 'number'
  ) {
    price = nextPlan.price;
  }

  return {
    price: price,
    currency: currency,
  };
};
